import type { components } from "@oathera/contract/api";
import type { Service } from "@oathera/contract";

/**
 * The live-stream wire types, taken STRAIGHT from the contract (design 8.1). The hub generates
 * these from `packages/contract/openapi.yaml`; the client must speak exactly them, so this module
 * only re-exports the generated shapes and never redefines a wire body. The hub side mirrors this
 * with `components["schemas"]` from `@oathera/contract/api` (`apps/core-edge/src/hub/types.ts`); the
 * web package depends on the same contract, so it imports the same way.
 *
 * The only logic here is a set of small, total type guards the client uses to narrow a parsed
 * `ServerFrame` by its `type` discriminator, because a frame arrives as untyped JSON off the socket.
 */

type Schemas = components["schemas"];

/** Topics the hub carries. */
export type StreamTopic = Schemas["StreamTopic"];

/** An opaque resume token. The client stores and echoes it; it NEVER parses or synthesises one. */
export type ResumeToken = Schemas["ResumeToken"];

/** A client-to-server frame: the only two the hub accepts. */
export type ClientFrame = Schemas["ClientFrame"];
export type SubscribeFrame = Schemas["SubscribeFrame"];
export type SnapshotRequestFrame = Schemas["SnapshotRequestFrame"];

/** A server-to-client frame (a WebSocket text frame, or the `data` of one SSE event). */
export type ServerFrame = Schemas["ServerFrame"];
export type SnapshotFrame = Schemas["SnapshotFrame"];
export type EventFrame = Schemas["EventFrame"];
export type MetricFrame = Schemas["MetricFrame"];
export type SourceFrame = Schemas["SourceFrame"];
export type AlertFrame = Schemas["AlertFrame"];

/** Why the hub sent a snapshot. The client discloses a gap for anything but `initial`. */
export type SnapshotReason = SnapshotFrame["reason"];

/** The redacted event body carried by an event frame / event snapshot. */
export type StreamEvent = Schemas["Event"];
/** The alert body carried by an alert snapshot (deltas carry only `rule_id` + `state`). */
export type StreamAlert = Schemas["Alert"];

export const STREAM_TOPICS = [
  "events",
  "metrics",
  "sources",
  "alerts",
] as const satisfies readonly StreamTopic[];

/** The hub's WebSocket close codes the client reacts to (design 8.1 "Slow clients"/"Bad frames"). */
export const CLOSE_SLOW_CLIENT = 1013;
export const CLOSE_POLICY = 1008; // bad_frame, or role / no-topics-remaining

/** The maximum size of a client frame the hub accepts; the client never produces a larger one. */
export const MAX_CLIENT_FRAME_CHARS = 4096;
/** A token over this length is a bad frame; the client never echoes one it did not receive. */
export const MAX_RESUME_TOKEN_CHARS = 512;

/**
 * Parse one inbound text frame into a `ServerFrame`, or return `null` when it is not a well-formed
 * server frame. The client treats a `null` as a protocol error on that frame rather than crashing:
 * the server is the authority, so a frame the client cannot read is dropped (and, on a socket, the
 * hub would have refused to send a malformed one — this guards SSE `data` and defends in depth).
 */
export function parseServerFrame(text: string): ServerFrame | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(value)) return null;
  const type = value["type"];
  if (type !== "snapshot" && type !== "event" && type !== "metric" && type !== "source" && type !== "alert") {
    return null;
  }
  // Every server frame carries a position and a resume token; a frame missing them is unusable
  // because the client resumes from the token. `pos` must be a number and `resume_token` a string
  // within the contract's bound.
  if (typeof value["pos"] !== "number") return null;
  const token = value["resume_token"];
  if (typeof token !== "string" || token.length > MAX_RESUME_TOKEN_CHARS) return null;
  return value as ServerFrame;
}

export function isSnapshotFrame(frame: ServerFrame): frame is SnapshotFrame {
  return frame.type === "snapshot";
}
export function isEventFrame(frame: ServerFrame): frame is EventFrame {
  return frame.type === "event";
}
export function isMetricFrame(frame: ServerFrame): frame is MetricFrame {
  return frame.type === "metric";
}
export function isSourceFrame(frame: ServerFrame): frame is SourceFrame {
  return frame.type === "source";
}
export function isAlertFrame(frame: ServerFrame): frame is AlertFrame {
  return frame.type === "alert";
}

/** A snapshot with a reason other than `initial` means the client must disclose a gap. */
export function snapshotDisclosesGap(reason: SnapshotReason): boolean {
  return reason !== "initial";
}

/** Build the `subscribe` frame, with the stored resume token when the client holds one. */
export function makeSubscribeFrame(options: {
  topics: readonly StreamTopic[];
  service: Service | null;
  resumeToken: string | null;
}): SubscribeFrame {
  const frame: SubscribeFrame = {
    type: "subscribe",
    topics: [...options.topics],
  };
  if (options.service !== null) frame.filters = { service: options.service };
  // Only echo a token the server gave us; never synthesise or edit one (design 8.1 "Resume tokens").
  if (options.resumeToken !== null) frame.resume_token = options.resumeToken;
  return frame;
}

/** Build a `snapshot_request` for one topic (sent after a pause-buffer overflow, FR-9.3). */
export function makeSnapshotRequestFrame(topic: StreamTopic): SnapshotRequestFrame {
  return { type: "snapshot_request", topic };
}

/** Serialise a client frame to the text the hub expects, or throw if it would exceed the bound. */
export function serializeClientFrame(frame: ClientFrame): string {
  const text = JSON.stringify(frame);
  if (text.length > MAX_CLIENT_FRAME_CHARS) {
    // The client builds its own frames, so this is a programming error, not a server condition.
    throw new RangeError(`client frame exceeds ${MAX_CLIENT_FRAME_CHARS} characters`);
  }
  return text;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
